import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/shared/Navbar';
import Footer from './components/shared/Footer';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CartPage from './pages/CartPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StudentDashboard from './pages/StudentDashboard';
import TestSeriesDetail from './pages/TestSeriesDetail';
import CBTEngine from './pages/CBTEngine';
import TestResults from './pages/TestResults';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import CategoriesManagement from './pages/admin/CategoriesManagement';
import TestSeriesManagement from './pages/admin/TestSeriesManagement';
import TestManagement from './pages/admin/TestManagement';
import QuestionManagement from './pages/admin/QuestionManagement';
import UserManagement from './pages/admin/UserManagement';
import SubAdminManagement from './pages/admin/SubAdminManagement';
import CouponManagement from './pages/admin/CouponManagement';
import SystemSettings from './pages/admin/SystemSettings';
import PagesManagement from './pages/admin/PagesManagement';
import AdminProfile from './pages/admin/AdminProfile';
import StaticPage from './pages/StaticPage';
import './App.css';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-[#7551e8] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin' && user.role !== 'root_admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'root_admin');

  return (
    <Routes>
      <Route path="/" element={<><Navbar /><HomePage /><Footer /></>} />
      <Route path="/login" element={<><Navbar /><LoginPage /><Footer /></>} />
      <Route path="/register" element={<><Navbar /><RegisterPage /><Footer /></>} />
      <Route path="/forgot-password" element={<><Navbar /><ForgotPasswordPage /><Footer /></>} />
      <Route path="/reset-password" element={<><Navbar /><ResetPasswordPage /><Footer /></>} />
      <Route path="/cart" element={<><Navbar /><CartPage /><Footer /></>} />
      <Route path="/test-series/:id" element={<><Navbar /><TestSeriesDetail /><Footer /></>} />
      <Route path="/page/:slug" element={<><Navbar /><StaticPage /><Footer /></>} />
      <Route path="/dashboard" element={<ProtectedRoute><Navbar /><StudentDashboard /><Footer /></ProtectedRoute>} />
      <Route path="/test/:attemptId" element={<ProtectedRoute><CBTEngine /></ProtectedRoute>} />
      <Route path="/results/:attemptId" element={<ProtectedRoute><Navbar /><TestResults /><Footer /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Navbar /><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="categories" element={<CategoriesManagement />} />
        <Route path="test-series" element={<TestSeriesManagement />} />
        <Route path="test-series/:seriesId/tests" element={<TestManagement />} />
        <Route path="tests/:testId/questions" element={<QuestionManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="coupons" element={<CouponManagement />} />
        <Route path="settings" element={<SystemSettings />} />
        <Route path="pages" element={<PagesManagement />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="sub-admins" element={<SubAdminManagement />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="min-h-screen flex flex-col">
            <AppRoutes />
          </div>
          <Toaster position="top-right" />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
